import { supabase } from './supabase';
import { db } from './localDb';
import { Database, ReviewResult, LessonStatus } from '../types/database';
import { isOnline } from '../utils/offline';
import { getSequentialPosition, setSequentialPosition } from './storage';
import { Table } from 'dexie';

type Card = Database['public']['Tables']['cards']['Row'];
type UserCardProgress = Database['public']['Tables']['user_card_progress']['Row'];
type StudySession = Database['public']['Tables']['study_sessions']['Row'];
type CardReviewHistory = Database['public']['Tables']['card_review_history']['Row'];
type Lesson = Database['public']['Tables']['lessons']['Row'];
type LessonCard = Database['public']['Tables']['lesson_cards']['Row'];
type UserLessonProgress = Database['public']['Tables']['user_lesson_progress']['Row'];
type Source = Database['public']['Tables']['sources']['Row'];
type Exercise = Database['public']['Tables']['exercises']['Row'];
type ExerciseLesson = Database['public']['Tables']['exercise_lessons']['Row'];

// ---------------------------------------------------------------------------
// Generic sync helpers — these replace ~12 nearly-identical private methods
// ---------------------------------------------------------------------------

/** Strip _syncStatus and _lastSyncError from a local record */
function clean<T>(record: any): T {
  const { _syncStatus, _lastSyncError, ...rest } = record;
  return rest as T;
}

/** Read all records from a local table, stripping sync metadata */
async function readAll<T>(table: Table<any>): Promise<T[]> {
  const records = await table.toArray();
  return records.map(r => clean<T>(r));
}

/** Read a single record by key, stripping sync metadata */
async function readOne<T>(table: Table<any>, key: any): Promise<T | null> {
  const record = await table.get(key);
  if (!record) return null;
  return clean<T>(record);
}

/** Write to local DB with pending status and queue for sync */
async function persist(
  table: Table<any>,
  key: any,
  data: any,
  remoteName: string,
  queueId: string,
  operation: 'create' | 'update',
): Promise<void> {
  if (operation === 'create') {
    await table.add({ ...data, _syncStatus: 'pending' });
  } else {
    await table.update(key, { ...data, _syncStatus: 'pending' });
  }
  await db.addToSyncQueue(remoteName, queueId, operation, data);
}

/** Mark a local record as pending-delete and queue for sync */
async function persistDeletion(
  table: Table<any>,
  key: any,
  remoteName: string,
  queueId: string,
  deleteData: any,
): Promise<void> {
  await table.update(key, { _syncStatus: 'pending' });
  await db.addToSyncQueue(remoteName, queueId, 'delete', deleteData);
}

/** Try to sync a single record to Supabase (upsert or insert) */
async function trySync(
  table: Table<any>,
  key: any,
  remoteName: string,
  queueId: string,
  method: 'upsert' | 'insert' = 'upsert',
): Promise<void> {
  try {
    const record = await table.get(key);
    if (!record || record._syncStatus !== 'pending') return;

    const data = clean(record);
    const { error } = method === 'insert'
      ? await supabase.from(remoteName).insert(data as any)
      : await supabase.from(remoteName).upsert(data as any);

    if (error) throw error;

    await table.update(key, { _syncStatus: 'synced' });
    await db.syncQueue
      .where('tableName').equals(remoteName)
      .and(item => item.recordId === queueId)
      .delete();
  } catch (error) {
    console.error(`Failed to sync ${remoteName}:`, error);
    await table.update(key, {
      _syncStatus: 'error',
      _lastSyncError: String(error),
    });
  }
}

/** Try to sync a deletion to Supabase */
async function trySyncDeletion(
  table: Table<any>,
  key: any,
  remoteName: string,
  queueId: string,
  filter: Record<string, string>,
): Promise<void> {
  try {
    let query = supabase.from(remoteName).delete();
    for (const [field, value] of Object.entries(filter)) {
      query = query.eq(field, value);
    }
    const { error } = await query;
    if (error) throw error;

    await table.delete(key);
    await db.syncQueue
      .where('tableName').equals(remoteName)
      .and(item => item.recordId === queueId)
      .delete();
  } catch (error) {
    console.error(`Failed to sync ${remoteName} deletion:`, error);
  }
}

/** Persist locally, then try immediate sync if online */
async function persistAndSync(
  table: Table<any>,
  key: any,
  data: any,
  remoteName: string,
  queueId: string,
  operation: 'create' | 'update',
  syncMethod: 'upsert' | 'insert' = 'upsert',
): Promise<void> {
  await persist(table, key, data, remoteName, queueId, operation);
  if (isOnline()) {
    trySync(table, key, remoteName, queueId, syncMethod).catch(console.error);
  }
}

/** Persist deletion locally, then try immediate sync if online */
async function persistDeletionAndSync(
  table: Table<any>,
  key: any,
  remoteName: string,
  queueId: string,
  deleteData: any,
  filter: Record<string, string>,
): Promise<void> {
  await persistDeletion(table, key, remoteName, queueId, deleteData);
  if (isOnline()) {
    trySyncDeletion(table, key, remoteName, queueId, filter).catch(console.error);
  }
}

// ---------------------------------------------------------------------------
// DataLayer — public API (unchanged interface, simplified implementation)
// ---------------------------------------------------------------------------

export class DataLayer {
  // ---- Cards ----

  static async getCards(): Promise<Card[]> {
    return readAll<Card>(db.cards);
  }

  static async createCard(
    card: Omit<Card, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'last_edited_by'>,
    userId: string,
  ): Promise<Card> {
    const newCard: Card = {
      ...card,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: userId,
      last_edited_by: userId,
    };
    await persistAndSync(db.cards, newCard.id, newCard, 'cards', newCard.id, 'create');
    return newCard;
  }

  static async cloneCard(originalCardId: string, userId: string): Promise<Card | null> {
    const cards = await this.getCards();
    const original = cards.find(c => c.id === originalCardId);
    if (!original) return null;

    return this.createCard({
      front: original.front,
      back: original.back,
      language: original.language,
      level: original.level,
      tags: [...original.tags],
      order: original.order,
      source_id: original.source_id,
      exercise_id: original.exercise_id,
      audio_url: original.audio_url,
    }, userId);
  }

  static async updateCard(id: string, updates: Partial<Card>, userId: string): Promise<void> {
    const data = { ...updates, updated_at: new Date().toISOString(), last_edited_by: userId };
    await persistAndSync(db.cards, id, data, 'cards', id, 'update');
  }

  static async deleteCard(id: string): Promise<void> {
    await persistDeletionAndSync(db.cards, id, 'cards', id, { id }, { id });
  }

  // ---- Sequential Study ----

  static async getCardsForSequentialStudy(
    filters: string[],
    userId: string,
  ): Promise<{ cards: Card[]; hasOrder: boolean }> {
    const allCards = await this.getCards();
    const filteredCards = allCards.filter(
      card =>
        filters.length === 0 ||
        filters.some(filter => card.tags.includes(filter) || card.exercise_id === filter),
    );

    const progress = await this.getUserCardProgress(userId);
    const progressMap = new Map(progress.map(p => [p.card_id, p]));

    const activeCards = filteredCards.filter(card => {
      const p = progressMap.get(card.id);
      return !p || (p.status !== 'mastered' && p.status !== 'hidden');
    });

    const hasOrder = activeCards.some(card => card.order !== null && card.order !== undefined);

    const sortedCards = [...activeCards].sort((a, b) => {
      if (hasOrder) {
        if (a.order === null || a.order === undefined) return 1;
        if (b.order === null || b.order === undefined) return -1;
        return a.order - b.order;
      }
      return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
    });

    return { cards: sortedCards, hasOrder };
  }

  static async getSequentialStudyPosition(userId: string, tags: string[]): Promise<number> {
    try {
      return getSequentialPosition(userId, tags);
    } catch {
      return 0;
    }
  }

  static async saveSequentialStudyPosition(
    userId: string,
    tags: string[],
    position: number,
  ): Promise<void> {
    try {
      setSequentialPosition(userId, tags, position);
    } catch (error) {
      console.error('Error saving sequential position:', error);
    }
  }

  // ---- User Card Progress ----

  static async getUserCardProgress(userId: string): Promise<UserCardProgress[]> {
    const localProgress = await db.userCardProgress
      .where('user_id')
      .equals(userId)
      .toArray();
    return localProgress.map(p => clean<UserCardProgress>(p));
  }

  static async upsertUserCardProgress(progress: UserCardProgress): Promise<void> {
    const key = [progress.user_id, progress.card_id];
    const queueId = `${progress.user_id}_${progress.card_id}`;
    const updatedProgress = { ...progress, updated_at: new Date().toISOString() };

    const existing = await db.userCardProgress.get(key);

    if (existing) {
      await persistAndSync(
        db.userCardProgress, key, updatedProgress,
        'user_card_progress', queueId, 'update',
      );
    } else {
      await persistAndSync(
        db.userCardProgress, key, updatedProgress,
        'user_card_progress', queueId, 'create',
      );
    }
  }

  // ---- Study Sessions ----

  static async createStudySession(session: Omit<StudySession, 'id'>): Promise<StudySession> {
    const newSession: StudySession = { ...session, id: crypto.randomUUID() };
    await persistAndSync(
      db.studySessions, newSession.id, newSession,
      'study_sessions', newSession.id, 'create',
    );
    return newSession;
  }

  static async updateStudySession(id: string, updates: Partial<StudySession>): Promise<void> {
    await persistAndSync(db.studySessions, id, updates, 'study_sessions', id, 'update');
  }

  // ---- Card Review History ----

  static async createReviewHistory(
    userId: string,
    cardId: string,
    result: ReviewResult,
    masteryScoreBefore: number,
    masteryScoreAfter: number,
    sessionId?: string,
  ): Promise<void> {
    const review: CardReviewHistory = {
      id: crypto.randomUUID(),
      user_id: userId,
      card_id: cardId,
      reviewed_at: new Date().toISOString(),
      result,
      mastery_score_before: masteryScoreBefore,
      mastery_score_after: masteryScoreAfter,
      session_id: sessionId || null,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    await persistAndSync(
      db.cardReviewHistory, review.id, review,
      'card_review_history', review.id, 'create', 'insert',
    );
  }

  static async getReviewHistory(
    userId: string,
    cardId: string,
    limit: number = 20,
  ): Promise<CardReviewHistory[]> {
    try {
      const localHistory = await db.cardReviewHistory
        .where('[user_id+card_id]')
        .equals([userId, cardId])
        .reverse()
        .sortBy('reviewed_at');
      return localHistory.slice(0, limit);
    } catch {
      return [];
    }
  }

  // ---- Lessons ----

  static async getLessons(isAdmin: boolean = false): Promise<Lesson[]> {
    const all = await readAll<Lesson>(db.lessons);
    const filtered = isAdmin ? all : all.filter(l => l.is_published);
    return filtered.sort((a, b) => a.order_index - b.order_index);
  }

  static async getLesson(id: string): Promise<Lesson | null> {
    return readOne<Lesson>(db.lessons, id);
  }

  static async getLessonBySlug(slug: string): Promise<Lesson | null> {
    const record = await db.lessons.where('slug').equals(slug).first();
    if (!record) return null;
    return clean<Lesson>(record);
  }

  static async createLesson(
    lesson: Omit<Lesson, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'last_edited_by'>,
    userId: string,
  ): Promise<Lesson> {
    const newLesson: Lesson = {
      ...lesson,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      created_by: userId,
      last_edited_by: userId,
    };
    await persistAndSync(db.lessons, newLesson.id, newLesson, 'lessons', newLesson.id, 'create');
    return newLesson;
  }

  static async updateLesson(id: string, updates: Partial<Lesson>, userId: string): Promise<void> {
    const data = { ...updates, updated_at: new Date().toISOString(), last_edited_by: userId };
    await persistAndSync(db.lessons, id, data, 'lessons', id, 'update');
  }

  static async deleteLesson(id: string): Promise<void> {
    await persistDeletionAndSync(db.lessons, id, 'lessons', id, { id }, { id });
  }

  // ---- Lesson Cards ----

  static async getLessonCards(lessonId: string): Promise<Card[]> {
    const lessonCards = await db.lessonCards
      .where('lesson_id')
      .equals(lessonId)
      .toArray();

    const cardIds = lessonCards.map(lc => lc.card_id);
    const cards = await db.cards.where('id').anyOf(cardIds).toArray();

    return cards
      .sort((a, b) => {
        const orderA = lessonCards.find(lc => lc.card_id === a.id)?.order_index || 0;
        const orderB = lessonCards.find(lc => lc.card_id === b.id)?.order_index || 0;
        return orderA - orderB;
      })
      .map(c => clean<Card>(c));
  }

  static async addCardToLesson(
    lessonId: string,
    cardId: string,
    orderIndex: number = 0,
  ): Promise<void> {
    const data: LessonCard = { lesson_id: lessonId, card_id: cardId, order_index: orderIndex };
    const queueId = `${lessonId}_${cardId}`;
    await persistAndSync(db.lessonCards, [lessonId, cardId], data, 'lesson_cards', queueId, 'create');
  }

  static async removeCardFromLesson(lessonId: string, cardId: string): Promise<void> {
    const queueId = `${lessonId}_${cardId}`;

    // Delete from local DB
    await db.lessonCards.where('[lesson_id+card_id]').equals([lessonId, cardId]).delete();
    await db.addToSyncQueue('lesson_cards', queueId, 'delete', { lesson_id: lessonId, card_id: cardId });

    if (isOnline()) {
      trySyncDeletion(
        db.lessonCards, [lessonId, cardId], 'lesson_cards', queueId,
        { lesson_id: lessonId, card_id: cardId },
      ).catch(console.error);
    }
  }

  // ---- User Lesson Progress ----

  static async getUserLessonProgress(
    userId: string,
    lessonId: string,
  ): Promise<UserLessonProgress | null> {
    const record = await db.userLessonProgress
      .where('[user_id+lesson_id]')
      .equals([userId, lessonId])
      .first();
    if (!record) return null;
    return clean<UserLessonProgress>(record);
  }

  static async updateUserLessonProgress(
    userId: string,
    lessonId: string,
    status: LessonStatus,
    completedAt?: string | null,
  ): Promise<void> {
    const existing = await db.userLessonProgress
      .where('[user_id+lesson_id]')
      .equals([userId, lessonId])
      .first();

    const data: UserLessonProgress = {
      user_id: userId,
      lesson_id: lessonId,
      status,
      completed_at: completedAt || null,
      last_accessed_at: new Date().toISOString(),
      created_at: existing?.created_at || new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };

    const key = [userId, lessonId];
    const queueId = `${userId}_${lessonId}`;
    const operation = existing ? 'update' : 'create';

    await persistAndSync(db.userLessonProgress, key, data, 'user_lesson_progress', queueId, operation);
  }

  // ---- Sources ----

  static async getSources(): Promise<Source[]> {
    return readAll<Source>(db.sources);
  }

  static async getSource(id: string): Promise<Source | null> {
    return readOne<Source>(db.sources, id);
  }

  static async getSourcesByCategory(category: string): Promise<Source[]> {
    const records = await db.sources.where('category').equals(category).toArray();
    return records.map(r => clean<Source>(r));
  }

  static async getParentSources(category: string): Promise<Source[]> {
    const all = await this.getSourcesByCategory(category);
    return all.filter(s => s.parent_id === null);
  }

  static async getChildSources(parentId: string): Promise<Source[]> {
    const records = await db.sources.where('parent_id').equals(parentId).toArray();
    return records.map(r => clean<Source>(r));
  }

  static async getCardsBySource(sourceId: string): Promise<Card[]> {
    const records = await db.cards.where('source_id').equals(sourceId).toArray();
    return records
      .map(r => clean<Card>(r))
      .sort((a, b) => {
        if (a.order !== null && b.order !== null) return a.order - b.order;
        if (a.order !== null) return -1;
        if (b.order !== null) return 1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }

  static async createSource(
    source: Omit<Source, 'id' | 'created_at' | 'updated_at'>,
  ): Promise<Source> {
    const newSource: Source = {
      ...source,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    await persistAndSync(db.sources, newSource.id, newSource, 'sources', newSource.id, 'create');
    return newSource;
  }

  static async updateSource(id: string, updates: Partial<Source>): Promise<void> {
    const data = { ...updates, updated_at: new Date().toISOString() };
    await persistAndSync(db.sources, id, data, 'sources', id, 'update');
  }

  static async deleteSource(id: string): Promise<void> {
    await persistDeletionAndSync(db.sources, id, 'sources', id, { id }, { id });
  }

  // ---- Exercises ----

  static async getExercises(): Promise<Exercise[]> {
    const all = await readAll<Exercise>(db.exercises);
    return all.sort((a, b) => a.order - b.order);
  }

  static async getExercise(id: string): Promise<Exercise | null> {
    return readOne<Exercise>(db.exercises, id);
  }

  static async getExerciseBySlug(slug: string): Promise<Exercise | null> {
    const record = await db.exercises.where('slug').equals(slug).first();
    if (!record) return null;
    return clean<Exercise>(record);
  }

  static async getExercisesByCategory(category: string): Promise<Exercise[]> {
    const records = await db.exercises.where('category').equals(category).toArray();
    return records.map(r => clean<Exercise>(r)).sort((a, b) => a.order - b.order);
  }

  static async getCardsByExercise(exerciseId: string): Promise<Card[]> {
    const records = await db.cards.where('exercise_id').equals(exerciseId).toArray();
    return records
      .map(r => clean<Card>(r))
      .sort((a, b) => {
        if (a.order !== null && b.order !== null) return a.order - b.order;
        if (a.order !== null) return -1;
        if (b.order !== null) return 1;
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      });
  }

  static async createExercise(
    exercise: Omit<Exercise, 'id' | 'created_at' | 'updated_at'>,
  ): Promise<Exercise> {
    const newExercise: Exercise = {
      ...exercise,
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
    };
    await persistAndSync(db.exercises, newExercise.id, newExercise, 'exercises', newExercise.id, 'create');
    return newExercise;
  }

  static async updateExercise(id: string, updates: Partial<Exercise>): Promise<void> {
    const data = { ...updates, updated_at: new Date().toISOString() };
    await persistAndSync(db.exercises, id, data, 'exercises', id, 'update');
  }

  static async deleteExercise(id: string): Promise<void> {
    await persistDeletionAndSync(db.exercises, id, 'exercises', id, { id }, { id });
  }

  // ---- Exercise-Lesson junction ----

  static async getExerciseLessons(exerciseId: string): Promise<ExerciseLesson[]> {
    const records = await db.exerciseLessons.where('exercise_id').equals(exerciseId).toArray();
    return records.map(r => clean<ExerciseLesson>(r)).sort((a, b) => a.order_index - b.order_index);
  }

  static async getLessonExercises(lessonId: string): Promise<ExerciseLesson[]> {
    const records = await db.exerciseLessons.where('lesson_id').equals(lessonId).toArray();
    return records.map(r => clean<ExerciseLesson>(r));
  }

  static async addExerciseToLesson(exerciseId: string, lessonId: string, orderIndex: number = 0): Promise<void> {
    const data: ExerciseLesson = { exercise_id: exerciseId, lesson_id: lessonId, order_index: orderIndex };
    const queueId = `${exerciseId}_${lessonId}`;
    await persistAndSync(db.exerciseLessons, [exerciseId, lessonId], data, 'exercise_lessons', queueId, 'create');
  }

  static async removeExerciseFromLesson(exerciseId: string, lessonId: string): Promise<void> {
    const queueId = `${exerciseId}_${lessonId}`;
    await db.exerciseLessons.where('[exercise_id+lesson_id]').equals([exerciseId, lessonId]).delete();
    await db.addToSyncQueue('exercise_lessons', queueId, 'delete', { exercise_id: exerciseId, lesson_id: lessonId });
    if (isOnline()) {
      trySyncDeletion(
        db.exerciseLessons, [exerciseId, lessonId], 'exercise_lessons', queueId,
        { exercise_id: exerciseId, lesson_id: lessonId },
      ).catch(console.error);
    }
  }
}
